import React, { useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { differenceInCalendarDays } from "date-fns";
import { Loader2, RefreshCw, SlidersHorizontal } from "lucide-react";
import type { ScheduleAuditAction, ScheduleAuditLog, ScheduleAuditSource } from "../../../../types";
import { AuditSidePanel } from "./AuditSidePanel";
import { AuditTimeline } from "./AuditTimeline";
import { AuditEmptyState, AuditErrorState, AuditSkeleton } from "./AuditStates";
import { ScheduleHistoryPanel } from "./ScheduleHistoryPanel";
import { useScheduleAuditLogs, type AuditLogsQuery } from "./useScheduleAuditLogs";
import { AUDIT_ACTION_OPTIONS, SOURCE_LABELS } from "./auditUtils";

const MAX_RANGE_DAYS = 92;

type Option = { value: string | number; label: string };

export interface ScheduleAuditDrawerProps {
    open: boolean;
    onClose: () => void;
    defaultStartDate: string;
    defaultEndDate: string;
    dateRangeLabel: string;
    loadEmployeeOptions: (inputValue: string) => Promise<Array<{ value: string | number; label: string; data?: unknown }>>;
}

interface Filters {
    startDate: string;
    endDate: string;
    actions: Option[];
    employee: Option | null;
    source: Option | null;
    actorId: string;
    batchId: string;
    order: "desc" | "asc";
}

const SOURCE_OPTIONS: Option[] = Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }));

const compactInput =
    "h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

const selectStyles = {
    control: (base: any) => ({ ...base, minHeight: 32, fontSize: 13 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 10000 }),
};

const validateRange = (start: string, end: string): string => {
    if (!start || !end) return "Choose a start and end date.";
    const days = differenceInCalendarDays(new Date(`${end}T00:00:00`), new Date(`${start}T00:00:00`));
    if (Number.isNaN(days)) return "Invalid date.";
    if (days < 0) return "End date can't be before start date.";
    if (days + 1 > MAX_RANGE_DAYS) return `Date range can't exceed ${MAX_RANGE_DAYS} days.`;
    return "";
};

const toQuery = (f: Filters): AuditLogsQuery => ({
    start_date: f.startDate,
    end_date: f.endDate,
    order: f.order,
    ...(f.actions.length ? { action: f.actions.map((a) => a.value as ScheduleAuditAction) } : {}),
    ...(f.employee ? { os_emp_account_id: String(f.employee.value) } : {}),
    ...(f.source ? { source: f.source.value as ScheduleAuditSource } : {}),
    ...(f.actorId.trim() ? { actor_id: f.actorId.trim() } : {}),
    ...(f.batchId.trim() ? { batch_id: f.batchId.trim() } : {}),
});

/** Whole-period audit: defaults to the scheduling tool's visible range, newest first. */
export const ScheduleAuditDrawer: React.FC<ScheduleAuditDrawerProps> = ({
    open,
    onClose,
    defaultStartDate,
    defaultEndDate,
    loadEmployeeOptions,
}) => {
    const makeDefaults = (): Filters => ({
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        actions: [],
        employee: null,
        source: null,
        actorId: "",
        batchId: "",
        order: "desc",
    });

    const [draft, setDraft] = useState<Filters>(makeDefaults);
    const [applied, setApplied] = useState<Filters | null>(null);
    const [showMore, setShowMore] = useState(false);
    const [historyLog, setHistoryLog] = useState<ScheduleAuditLog | null>(null);

    // Reset to the currently visible range every time the drawer opens.
    useEffect(() => {
        if (!open) {
            setApplied(null);
            setHistoryLog(null);
            return;
        }
        const defaults = makeDefaults();
        setDraft(defaults);
        setApplied(defaults);
        setShowMore(false);
        // Default dates are read at open time only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const rangeError = validateRange(draft.startDate, draft.endDate);
    const query = useMemo(() => (applied ? toQuery(applied) : null), [applied]);
    const { logs, meta, isLoading, isLoadingMore, error, loadMore, refresh } = useScheduleAuditLogs(query, 50);

    const isDirty = !applied || JSON.stringify(toQuery(draft)) !== JSON.stringify(toQuery(applied));
    const isDefaultRange = draft.startDate === defaultStartDate && draft.endDate === defaultEndDate;
    const activeFilterCount =
        draft.actions.length + (draft.employee ? 1 : 0) + (draft.source ? 1 : 0) + (draft.actorId ? 1 : 0) + (draft.batchId ? 1 : 0);

    const apply = () => {
        if (!rangeError) setApplied({ ...draft });
    };
    const reset = () => {
        const defaults = makeDefaults();
        setDraft(defaults);
        setApplied(defaults);
    };
    const set = <K extends keyof Filters>(key: K, value: Filters[K]) => setDraft((prev) => ({ ...prev, [key]: value }));

    const toolbar = (
        <form
            className="space-y-2"
            onSubmit={(e) => {
                e.preventDefault();
                apply();
            }}
        >
            {/* Compact bar: range + filters toggle + apply */}
            <p className="text-xs font-medium text-gray-600">Changes made between</p>
            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="date"
                    aria-label="Changes made from date"
                    value={draft.startDate}
                    onChange={(e) => set("startDate", e.target.value)}
                    className={`${compactInput} w-[9.5rem]`}
                />
                <span className="text-xs text-gray-400">to</span>
                <input
                    type="date"
                    aria-label="Changes made to date"
                    value={draft.endDate}
                    min={draft.startDate}
                    onChange={(e) => set("endDate", e.target.value)}
                    className={`${compactInput} w-[9.5rem]`}
                />
                <button
                    type="button"
                    onClick={() => setShowMore((v) => !v)}
                    aria-expanded={showMore}
                    className="flex h-8 items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                    <SlidersHorizontal size={14} />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold leading-4 text-white">{activeFilterCount}</span>
                    )}
                </button>
                <div className="ml-auto flex items-center gap-2">
                    {(activeFilterCount > 0 || !isDefaultRange || draft.order !== "desc") && (
                        <button type="button" onClick={reset} className="text-xs font-medium text-gray-500 hover:text-gray-800">
                            Reset
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={!!rangeError || isLoading || !isDirty}
                        className="h-8 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Apply
                    </button>
                </div>
            </div>
            {rangeError && <p className="text-xs text-red-600">{rangeError}</p>}

            {showMore && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                    <div className="col-span-2">
                        <Select
                            isMulti
                            value={draft.actions}
                            onChange={(opts) => set("actions", (opts as Option[]) || [])}
                            options={AUDIT_ACTION_OPTIONS}
                            placeholder="All actions"
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                        />
                    </div>
                    <div className="col-span-2">
                        <AsyncSelect
                            cacheOptions
                            defaultOptions={false}
                            isClearable
                            value={draft.employee}
                            loadOptions={loadEmployeeOptions}
                            onChange={(opt) => set("employee", (opt as Option) || null)}
                            placeholder="Employee (type 3+ characters)"
                            noOptionsMessage={({ inputValue }) => (inputValue.length < 3 ? "Type at least 3 characters" : "No employees found")}
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                        />
                    </div>
                    <Select
                        isClearable
                        value={draft.source}
                        onChange={(opt) => set("source", (opt as Option) || null)}
                        options={SOURCE_OPTIONS}
                        placeholder="Any source"
                        menuPortalTarget={document.body}
                        styles={selectStyles}
                    />
                    <select
                        aria-label="Order"
                        value={draft.order}
                        onChange={(e) => set("order", e.target.value as "asc" | "desc")}
                        className={compactInput}
                    >
                        <option value="desc">Newest first</option>
                        <option value="asc">Oldest first</option>
                    </select>
                    <input
                        aria-label="Changed by login id"
                        value={draft.actorId}
                        onChange={(e) => set("actorId", e.target.value)}
                        placeholder="Changed by (login id)"
                        className={compactInput}
                    />
                    <input
                        aria-label="Batch id"
                        value={draft.batchId}
                        onChange={(e) => set("batchId", e.target.value)}
                        placeholder="Batch id"
                        className={compactInput}
                    />
                </div>
            )}
        </form>
    );

    return (
        <>
            <AuditSidePanel
                open={open}
                onClose={onClose}
                title="Schedule audit"
                subtitle="Changes made within the selected dates (not the shift dates)"
                widthClass="sm:max-w-[560px]"
                zIndexClass="z-[9999]"
                toolbar={toolbar}
                disableEscape={!!historyLog}
                actions={
                    <button
                        type="button"
                        onClick={refresh}
                        disabled={isLoading || !applied}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                        aria-label="Refresh audit logs"
                        title="Refresh"
                    >
                        <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
                    </button>
                }
                footer={
                    meta && logs.length > 0 ? (
                        <p className="text-center text-xs text-gray-400">
                            Showing {logs.length} of {meta.total}
                            {meta.timezone ? ` · times in ${meta.timezone}` : ""}
                        </p>
                    ) : undefined
                }
            >
                {isLoading ? (
                    <AuditSkeleton rows={5} />
                ) : error ? (
                    <AuditErrorState message={error} onRetry={refresh} />
                ) : logs.length === 0 ? (
                    <AuditEmptyState />
                ) : (
                    <>
                        <AuditTimeline logs={logs} timezone={meta?.timezone} groupByDay showEmployee onViewHistory={setHistoryLog} />
                        {meta?.has_more && (
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={isLoadingMore}
                                className="mt-5 flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                                {isLoadingMore && <Loader2 size={14} className="animate-spin" />}
                                Load more
                            </button>
                        )}
                    </>
                )}
            </AuditSidePanel>

            <ScheduleHistoryPanel
                open={open && !!historyLog}
                onClose={() => setHistoryLog(null)}
                scheduleId={historyLog?.os_emp_schedules_id}
                subtitle={historyLog?.employee_name || undefined}
                zIndexClass="z-[10000]"
            />
        </>
    );
};

export default ScheduleAuditDrawer;
